import { useEffect, useRef } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth, ROLE_HOME, type AppRole } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";

interface ProtectedRouteProps {
  children: React.ReactNode;
  requireAdmin?: boolean;
  requireRole?: AppRole;
  requireRoles?: AppRole[];
  /** If true, do not enforce onboarding gate (e.g. on /onboarding itself). */
  skipOnboarding?: boolean;
}

export default function ProtectedRoute({
  children,
  requireAdmin = false,
  requireRole,
  requireRoles,
  skipOnboarding = false,
}: ProtectedRouteProps) {
  const { user, isAdmin, role, loading } = useAuth();
  const location = useLocation();
  const { toast } = useToast();
  const notifiedRef = useRef(false);

  const unauthenticated = !loading && !user;
  const allowedRoles = requireRoles ?? (requireRole ? [requireRole] : undefined);
  const wrongRole =
    !loading && !!user && (
      (requireAdmin && !isAdmin) ||
      (!!allowedRoles && (!role || !allowedRoles.includes(role)))
    );

  useEffect(() => {
    if (notifiedRef.current) return;
    if (unauthenticated) {
      notifiedRef.current = true;
      toast({ variant: "destructive", title: "Sign in required", description: "Please sign in to access this page." });
    } else if (wrongRole) {
      notifiedRef.current = true;
      toast({ variant: "destructive", title: "Unauthorized Access", description: "You don't have permission to view this page." });
    }
  }, [unauthenticated, wrongRole, toast]);

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }

  if (wrongRole) {
    const home = role ? ROLE_HOME[role] : "/dashboard";
    return <Navigate to={home} replace />;
  }

  return <>{children}</>;
}
